import { z } from "zod";

/**
 * Body of POST /api/generation-jobs — the "Generate Selected" action (§11).
 *
 * `collectionId` is required: it determines which knowledge base and model
 * the worker uses, and the selected categories must all belong to it — a
 * batch cannot mix categories from two different collections in one request.
 *
 * A target is a topic or a subchapter. At least one of topicIds/subChapterIds
 * must be non-empty — some subchapters (an "Introduction" with no topics
 * under it) have nothing else to generate for.
 */
export const createJobsSchema = z
  .object({
    collectionId: z.number().int().positive(),
    // Topic/subchapter ids are the main app's UUIDs (§ main-app integration) —
    // ZenForge no longer mints its own numeric ids for these.
    topicIds: z.array(z.string().uuid()).default([]),
    subChapterIds: z.array(z.string().uuid()).default([]),
    categoryIds: z.array(z.number().int().positive()).min(1, "Select at least one category"),
    // No allowGeneralKnowledge and no knowledgeSourceIds: generation is
    // grounded in whichever PDF the target's chapter/subchapter is mapped to
    // (migration 019), and there is no longer a way to opt out of that or to
    // hand-pick sources for a run.
    // Pushes each job's content to the main app immediately after it
    // generates, instead of waiting for a person to review and click Approve.
    autoApprove: z.boolean().default(false),
    /** Run on a specific model instead of the collection's default.
     *
     *  Stamped on every job created (migration 029) rather than read from the
     *  collection when the job runs, which does two things: work already
     *  queued is immune to a later collection edit, and the same topic can be
     *  queued twice on two models to compare them — the model is part of what
     *  makes two runs different, so the second is not skipped as a duplicate.
     *
     *  Null or omitted keeps the old behaviour: the collection decides. */
    model: z.string().min(1).nullable().optional(),
  })
  .refine((input) => input.topicIds.length > 0 || input.subChapterIds.length > 0, {
    message: "Select at least one topic or subchapter",
    path: ["topicIds"],
  });

export type CreateJobsInput = z.infer<typeof createJobsSchema>;

/** Body of PUT /api/generated-contents/:id — reviewer edits.
 *  reviewedMobileContent is optional: omitted leaves whatever is stored
 *  untouched, null explicitly clears a mobile-specific edit (reverting to
 *  mirroring reviewedContent), a string sets a distinct mobile edit. */
export const updateContentSchema = z.object({
  reviewedContent: z.string().min(1, "Content cannot be empty"),
  reviewedMobileContent: z.string().min(1, "Content cannot be empty").nullable().optional(),
});
